import { Moon, Sun } from "lucide-react";
import { IconButton } from "@/components/ui/IconButton";
import { useTheme } from "@/lib/theme";

/**
 * Light / dark, in the console's top bar.
 *
 * **The icon shows the action, not the state.** In dark mode it is a sun,
 * because pressing it produces daylight. The previous version showed a moon in
 * dark mode while its own label read "switch to light theme" - the picture and
 * the words describing the same control disagreed, which is the kind of thing
 * that makes people press a button twice to work out what it does.
 *
 * Two states, because the provider now has exactly two - see lib/theme.tsx for
 * why "system" was removed rather than hidden behind this control.
 *
 * The shape and the two surface tones live in `IconButton`; this component is
 * only the theme decision and the label that describes it.
 */
export function ThemeToggle({
  className,
  tone = "default",
}: {
  className?: string;
  tone?: "default" | "panel";
}) {
  const { theme, toggle } = useTheme();
  const goingDark = theme !== "dark";

  return (
    <IconButton
      onClick={toggle}
      tone={tone}
      label={goingDark ? "Switch to dark theme" : "Switch to light theme"}
      className={className}
    >
      {goingDark ? <Moon /> : <Sun />}
    </IconButton>
  );
}
